import React, { useEffect, useState, Children } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import { LanguageProvider } from './i18n';
import { HomePage } from './pages/HomePage';
import { ServicesPage } from './pages/ServicesPage';
import { ServiceDetailPage } from './pages/ServiceDetailPage';
import { AboutPage } from './pages/AboutPage';
import { ContactPage } from './pages/ContactPage';
import { PricesPage } from './pages/PricesPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { CookiePolicyPage } from './pages/CookiePolicyPage';
import { PatientPrivacyNoticePage } from './pages/PatientPrivacyNoticePage';
import { TermsAndConditionsPage } from './pages/TermsAndConditionsPage';
import { BusinessCardPage } from './pages/BusinessCardPage';
import { UkrainianDentistPage } from './pages/UkrainianDentistPage';
import { ImplantyLondonPage } from './pages/ImplantyLondonPage';
import { VeneersLondonPage } from './pages/VeneersLondonPage';
import { RootCanalLondonPage } from './pages/RootCanalLondonPage';
import { EmergencyDentistLondonPage } from './pages/EmergencyDentistLondonPage';
import { RussianDentistPage } from './pages/RussianDentistPage';
import { ImplantyLondonRuPage } from './pages/ImplantyLondonRuPage';
import { VeneersLondonRuPage } from './pages/VeneersLondonRuPage';
import { RootCanalLondonRuPage } from './pages/RootCanalLondonRuPage';
import { EmergencyDentistLondonRuPage } from './pages/EmergencyDentistLondonRuPage';
import { BlogIndexPage } from './pages/blog/BlogIndexPage';
import { ImplantyCostArticle } from './pages/blog/ImplantyCostArticle';
import { NhsVsPrivateArticle } from './pages/blog/NhsVsPrivateArticle';
import { WithoutInsuranceArticle } from './pages/blog/WithoutInsuranceArticle';
import { ToothPainArticle } from './pages/blog/ToothPainArticle';
import { VeneersGuideArticle } from './pages/blog/VeneersGuideArticle';
import { ChooseDentistArticle } from './pages/blog/ChooseDentistArticle';
import { SitemapPage } from './pages/SitemapPage';
import { OrthodonticsPage } from './pages/OrthodonticsPage';
import { ClearAlignersPage } from './pages/ClearAlignersPage';
import { InvisalignAlignersPage } from './pages/InvisalignAlignersPage';
import { MetalBracesPage } from './pages/MetalBracesPage';
import { CeramicBracesPage } from './pages/CeramicBracesPage';
import { RetainersPage } from './pages/RetainersPage';
import { ChildrensOrthodonticsPage } from './pages/ChildrensOrthodonticsPage';
import { GiftCertificate } from './components/GiftCertificate';
import { PrintPricePage } from './pages/PrintPricePage';
import { InfluencerInvitePage } from './pages/InfluencerInvitePage';
import { RaffleVoucherPage } from './pages/RaffleVoucherPage';
import { LogoPage } from './pages/LogoPage';
import { OrthodonticFlyerUaPage } from './pages/OrthodonticFlyerUaPage';
import { PaediatricDentistryPage } from './pages/PaediatricDentistryPage';
import { WhatsAppQrPage } from './pages/WhatsAppQrPage';
import { PatientExperiencePage } from './pages/PatientExperiencePage';
import { ResultsPage } from './pages/ResultsPage';
import { ReviewsPage } from './pages/ReviewsPage';
// ── Global scroll-to-top on every route change ──
// Exception: /prices#anchor — let PricesPage handle its own hash scrolling
function ScrollToTop() {
  const {
    pathname,
    hash
  } = useLocation();
  useEffect(() => {
    // Disable browser scroll restoration globally
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);
  useEffect(() => {
    // If navigating to /prices with a hash, skip — PricesPage handles it
    const isPricesWithHash = pathname.includes('/prices') && hash;
    if (isPricesWithHash) return;
    // Immediate scroll
    window.scrollTo(0, 0);
    // Reliable delayed scroll for dynamic content / mobile
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });
    const t = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 50);
    return () => clearTimeout(t);
  }, [pathname, hash]);
  return null;
}
// Detect user's preferred language from browser
function detectUserLanguage(): 'en' | 'ua' | 'ru' | 'ro' {
  const browserLangs = navigator.languages || [navigator.language];
  for (const lang of browserLangs) {
    const langCode = lang.toLowerCase().split('-')[0];
    if (langCode === 'uk' || langCode === 'ua') {
      return 'ua';
    }
    if (langCode === 'ru') {
      return 'ru';
    }
    if (langCode === 'ro') {
      return 'ro';
    }
    if (langCode === 'en') {
      return 'en';
    }
  }
  return 'en';
}
function getStoredLanguage(): 'en' | 'ua' | 'ru' | 'ro' | null {
  try {
    const stored = localStorage.getItem('dentico-preferred-lang');
    if (stored === 'en' || stored === 'ua' || stored === 'ru' || stored === 'ro') {
      return stored as 'en' | 'ua' | 'ru' | 'ro';
    }
  } catch {
    // localStorage not available
  }
  return null;
}
function setStoredLanguage(lang: 'en' | 'ua' | 'ru' | 'ro') {
  try {
    localStorage.setItem('dentico-preferred-lang', lang);
  } catch {
    // localStorage not available
  }
}
function LanguageRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    const storedLang = getStoredLanguage();
    if (storedLang) {
      navigate(`/${storedLang}`, {
        replace: true
      });
      return;
    }
    const detectedLang = detectUserLanguage();
    setStoredLanguage(detectedLang);
    navigate(`/${detectedLang}`, {
      replace: true
    });
  }, [navigate]);
  return null;
}
function LanguageRoutes() {
  const {
    lang
  } = useParams();
  const validLangs = ['en', 'ua', 'ru', 'ro'];
  if (!validLangs.includes(lang || '')) {
    return <Navigate to="/en" replace data-id="element-0" />;
  }
  useEffect(() => {
    if (lang && validLangs.includes(lang)) {
      setStoredLanguage(lang as 'en' | 'ua' | 'ru' | 'ro');
    }
  }, [lang]);
  return <LanguageProvider data-id="element-1">
      <Routes data-id="element-2">
        <Route index element={<HomePage data-id="element-4" />} data-id="element-3" />
        <Route path="ukrainskyi-stomatolog-london" element={<UkrainianDentistPage data-id="element-6" />} data-id="element-5" />
        <Route path="implanty-london" element={<ImplantyLondonPage data-id="element-8" />} data-id="element-7" />
        <Route path="veneers-london" element={<VeneersLondonPage data-id="element-10" />} data-id="element-9" />
        <Route path="root-canal-london" element={<RootCanalLondonPage data-id="element-12" />} data-id="element-11" />
        <Route path="emergency-dentist-london" element={<EmergencyDentistLondonPage data-id="element-14" />} data-id="element-13" />
        <Route path="russkij-stomatolog-london" element={<RussianDentistPage data-id="element-16" />} data-id="element-15" />
        <Route path="implantaciya-london" element={<ImplantyLondonRuPage data-id="element-18" />} data-id="element-17" />
        <Route path="viniry-london" element={<VeneersLondonRuPage data-id="element-20" />} data-id="element-19" />
        <Route path="lechenie-kanalov-london" element={<RootCanalLondonRuPage data-id="element-22" />} data-id="element-21" />
        <Route path="neotlozhnaya-stomatologiya-london" element={<EmergencyDentistLondonRuPage data-id="element-24" />} data-id="element-23" />
        <Route path="blog" element={<BlogIndexPage data-id="element-26" />} data-id="element-25" />
        <Route path="blog/dental-implants-cost-london" element={<ImplantyCostArticle data-id="element-28" />} data-id="element-27" />
        <Route path="blog/nhs-vs-private-dentistry-uk" element={<NhsVsPrivateArticle data-id="element-30" />} data-id="element-29" />
        <Route path="blog/dental-treatment-uk-without-insurance" element={<WithoutInsuranceArticle data-id="element-32" />} data-id="element-31" />
        <Route path="blog/toothache-london-what-to-do" element={<ToothPainArticle data-id="element-34" />} data-id="element-33" />
        <Route path="blog/veneers-london-everything-you-need-to-know" element={<VeneersGuideArticle data-id="element-36" />} data-id="element-35" />
        <Route path="blog/how-to-choose-dentist-angel" element={<ChooseDentistArticle data-id="element-38" />} data-id="element-37" />
        <Route path="sitemap" element={<SitemapPage data-id="element-40" />} data-id="element-39" />
        <Route path="services" element={<ServicesPage data-id="element-42" />} data-id="element-41" />
        <Route path="services/paediatric-dentistry" element={<PaediatricDentistryPage data-id="element-44" />} data-id="element-43" />
        <Route path="services/orthodontics" element={<OrthodonticsPage data-id="element-46" />} data-id="element-45" />
        <Route path="services/orthodontics/clear-aligners" element={<ClearAlignersPage data-id="element-48" />} data-id="element-47" />
        <Route path="services/orthodontics/invisalign-style-aligners" element={<InvisalignAlignersPage data-id="element-50" />} data-id="element-49" />
        <Route path="services/orthodontics/metal-braces" element={<MetalBracesPage data-id="element-52" />} data-id="element-51" />
        <Route path="services/orthodontics/ceramic-braces" element={<CeramicBracesPage data-id="element-54" />} data-id="element-53" />
        <Route path="services/orthodontics/retainers" element={<RetainersPage data-id="element-56" />} data-id="element-55" />
        <Route path="services/orthodontics/children" element={<ChildrensOrthodonticsPage data-id="element-58" />} data-id="element-57" />
        <Route path="services/:serviceId" element={<ServiceDetailPage data-id="element-60" />} data-id="element-59" />
        <Route path="prices" element={<PricesPage data-id="element-62" />} data-id="element-61" />
        <Route path="about" element={<AboutPage data-id="element-64" />} data-id="element-63" />
        <Route path="patient-experience" element={<PatientExperiencePage data-id="element-66" />} data-id="element-65" />
        <Route path="results" element={<ResultsPage data-id="element-68" />} data-id="element-67" />
        <Route path="reviews" element={<ReviewsPage data-id="element-70" />} data-id="element-69" />
        <Route path="contact" element={<ContactPage data-id="element-72" />} data-id="element-71" />
        <Route path="privacy-policy" element={<PrivacyPolicyPage data-id="element-74" />} data-id="element-73" />
        <Route path="cookie-policy" element={<CookiePolicyPage data-id="element-76" />} data-id="element-75" />
        <Route path="patient-privacy-notice" element={<PatientPrivacyNoticePage data-id="element-78" />} data-id="element-77" />
        <Route path="terms-and-conditions" element={<TermsAndConditionsPage data-id="element-80" />} data-id="element-79" />
        <Route path="*" element={<Navigate to={`/${lang}`} replace data-id="element-82" />} data-id="element-81" />
      </Routes>
    </LanguageProvider>;
}
export function App() {
  return <BrowserRouter data-id="element-83">
      <ScrollToTop data-id="element-84" />
      <Routes data-id="element-85">
        <Route path="/business-card" element={<BusinessCardPage data-id="element-87" />} data-id="element-86" />
        <Route path="/gift-certificate" element={<GiftCertificate data-id="element-89" />} data-id="element-88" />
        <Route path="/price-list" element={<PrintPricePage data-id="element-91" />} data-id="element-90" />
        <Route path="/influencer-invite" element={<InfluencerInvitePage data-id="element-93" />} data-id="element-92" />
        <Route path="/raffle-voucher" element={<RaffleVoucherPage data-id="element-95" />} data-id="element-94" />
        <Route path="/whatsapp-qr" element={<WhatsAppQrPage data-id="element-97" />} data-id="element-96" />
        <Route path="/logo" element={<LogoPage data-id="element-99" />} data-id="element-98" />
        <Route path="/orthodontic-flyer-ua" element={<OrthodonticFlyerUaPage data-id="element-101" />} data-id="element-100" />
        <Route path="/" element={<LanguageRedirect data-id="element-103" />} data-id="element-102" />
        <Route path="/:lang/*" element={<LanguageRoutes data-id="element-105" />} data-id="element-104" />
        <Route path="*" element={<LanguageRedirect data-id="element-107" />} data-id="element-106" />
      </Routes>
    </BrowserRouter>;
}